import { useEffect, useState } from 'react';
import type { ArtikelAnsicht, Gesellschaft, TaetigkeitsartAnsicht } from '@arbeitsrapporte/shared';
import { useIch, useIchAktualisieren } from '../App';
import { api } from '../api';
import { Artikelwahl } from '../bausteine/Artikelwahl';
import { Fehler } from '../bausteine/Meldung';

/**
 * Wofür verrechnet wird – die Zuordnung Rolle → Artikelnummer.
 *
 * **Eine Zeile je Rolle und Gesellschaft.** Die Artikelnummern von CH und DE
 * sind verschieden; wer „Monteur" in beiden führt, legt ihn zweimal an. Das
 * ist keine Doppelung, sondern zwei verschiedene Artikel.
 *
 * Gelesen darf jeder – der Techniker wählt die Rolle bei jeder Stundenzeile.
 * Gepflegt wird sie von der Leitung: Eine falsche Artikelnummer ist eine
 * falsche Rechnung.
 */

const GESELLSCHAFTEN: Gesellschaft[] = ['CH', 'DE'];

function Anlegen({ fertig }: { fertig: () => void }) {
  const [gesellschaft, setGesellschaft] = useState<Gesellschaft>('CH');
  const [bezeichnung, setBezeichnung] = useState('');
  const [art, setArt] = useState<'arbeit' | 'reise'>('arbeit');
  const [artikel, setArtikel] = useState<ArtikelAnsicht | null>(null);
  const [einheit, setEinheit] = useState('h');
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const anlegen = async () => {
    if (!artikel) return;

    setLaeuft(true);
    setFehler(null);

    try {
      await api.legeTaetigkeitsartAn({
        gesellschaft,
        bezeichnung,
        art,
        artikelId: artikel.id,
        einheit,
      });
      setBezeichnung('');
      setArtikel(null);
      fertig();
    } catch (f) {
      setFehler(f);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="karte mt-3 space-y-4">
      <h2 className="text-lg font-semibold">Neue Tätigkeitsart</h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="feldname" htmlFor="neu-gesellschaft">
            Gesellschaft
          </label>
          <select
            id="neu-gesellschaft"
            className="feld"
            value={gesellschaft}
            onChange={(e) => {
              setGesellschaft(e.target.value as Gesellschaft);
              /* Der Artikel gehört zur Gesellschaft – die Wahl gilt nicht weiter. */
              setArtikel(null);
            }}
          >
            {GESELLSCHAFTEN.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="feldname" htmlFor="neu-bezeichnung">
            Bezeichnung
          </label>
          <input
            id="neu-bezeichnung"
            className="feld"
            placeholder="Monteur, Projektleiter, Fahrzeug km"
            value={bezeichnung}
            onChange={(e) => setBezeichnung(e.target.value)}
          />
        </div>

        <div>
          <label className="feldname" htmlFor="neu-art">
            Gilt für
          </label>
          <select
            id="neu-art"
            className="feld"
            value={art}
            onChange={(e) => {
              const gewaehlt = e.target.value as 'arbeit' | 'reise';

              setArt(gewaehlt);
              setEinheit(gewaehlt === 'reise' ? 'km' : 'h');
            }}
          >
            <option value="arbeit">Arbeit</option>
            <option value="reise">Reise</option>
          </select>
        </div>

        <div>
          <label className="feldname" htmlFor="neu-einheit">
            Einheit
          </label>
          <input
            id="neu-einheit"
            className="feld"
            value={einheit}
            onChange={(e) => setEinheit(e.target.value)}
          />
        </div>
      </div>

      <div>
        <span className="feldname">Artikel</span>
        <Artikelwahl
          gesellschaft={gesellschaft}
          gewaehlt={artikel ? { id: artikel.id, nummer: artikel.nummer } : null}
          onWahl={setArtikel}
        />
        <p className="mt-1 text-sm text-slate-500">
          Der Artikel kommt aus dem ERP und wird gewählt, nicht getippt. Seine Nummer ist das, was
          mit der Stunde ins ERP geht.
        </p>
      </div>

      <Fehler fehler={fehler} />

      <button
        type="button"
        className="knopf-haupt"
        disabled={laeuft || !bezeichnung.trim() || !artikel || !einheit.trim()}
        onClick={anlegen}
      >
        Anlegen
      </button>
    </div>
  );
}

/**
 * Die Pflege – nur für die Leitung, und deshalb in den Einstellungen
 * beheimatet (`seiten/Einstellungen.tsx`).
 *
 * Als eigener Baustein und nicht als eigene Seite: Das Anlegeformular und
 * die Liste gehören zusammen, und das Laden macht die Seite, die sie
 * zeigt – sie lädt ohnehin noch drei andere Dinge.
 */
export function Taetigkeitsartenpflege({
  arten,
  fertig,
}: {
  arten: TaetigkeitsartAnsicht[] | null;
  fertig: () => void;
}) {
  const [anlegen, setAnlegen] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);

  const umschalten = async (zeile: TaetigkeitsartAnsicht) => {
    setFehler(null);

    try {
      await api.aendereTaetigkeitsart(zeile.id, { deaktiviert: !zeile.deaktiviertAm });
      fertig();
    } catch (f) {
      setFehler(f);
    }
  };

  return (
    <>
      <p className="mb-3 text-slate-600">
        Wofür Stunden und Kilometer verrechnet werden. Der Techniker wählt die Rolle, die
        Artikelnummer kommt aus ihr –{' '}
        <b className="font-semibold text-slate-900">je Gesellschaft eine eigene Zeile</b>, denn die
        Nummern von CH und DE sind verschieden.
      </p>

      {anlegen ? (
        <Anlegen
          fertig={() => {
            setAnlegen(false);
            fertig();
          }}
        />
      ) : (
        <button type="button" className="knopf-haupt" onClick={() => setAnlegen(true)}>
          Neue Tätigkeitsart
        </button>
      )}

      <div className="mt-4">
        <Fehler fehler={fehler} />

        {arten === null && <p className="text-slate-600">Lädt …</p>}
        {arten?.length === 0 && (
          <p className="text-slate-600">
            Noch keine angelegt. Bis dahin gehen Stunden ohne Artikelnummer ins ERP.
          </p>
        )}

        <ul className="space-y-2">
          {arten?.map((t) => (
            /*
              Eine außer Kraft gesetzte Zeile bleibt stehen und wird blass:
              An alten Rapporten hängt sie noch, und wer sie sucht, soll sie
              finden.
            */
            <li key={t.id} className={`karte ${t.deaktiviertAm ? 'opacity-60' : ''}`}>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-slate-900">{t.bezeichnung}</span>
                <span className="ml-auto shrink-0 rounded border border-slate-300 px-2 py-0.5 text-xs tracking-wide text-slate-500 uppercase">
                  {t.gesellschaft}
                </span>
              </div>
              <div className="mt-1 text-slate-700">
                Artikel <span className="font-medium">{t.artikelNummer}</span> ·{' '}
                {t.art === 'reise' ? 'Reise' : 'Arbeit'} · {t.einheit}
              </div>
              {t.deaktiviertAm && (
                <div className="mt-0.5 text-sm text-slate-500">
                  außer Kraft seit {t.deaktiviertAm.slice(0, 10)}
                </div>
              )}
              <button
                type="button"
                onClick={() => void umschalten(t)}
                className="mt-2 min-h-0 px-0 text-sm text-slate-500 underline underline-offset-4 hover:text-slate-900"
              >
                {t.deaktiviertAm ? 'Wieder in Kraft setzen' : 'Außer Kraft setzen'}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

/**
 * Was der Techniker hier tut: seine Vorbelegung wählen, und nachsehen,
 * welche Rollen es gibt.
 *
 * **Die Pflege steht nicht mehr hier**, sondern in den Einstellungen – sie
 * ist Sache der Leitung und wird ein paar Mal im Jahr angefasst, während
 * diese Seite jeden betrifft, der Stunden schreibt.
 */
export function Taetigkeitsarten() {
  const ich = useIch();
  const aktualisiereIch = useIchAktualisieren();
  const [daten, setDaten] = useState<TaetigkeitsartAnsicht[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [gespeichert, setGespeichert] = useState(false);

  useEffect(() => {
    api.taetigkeitsarten().then(setDaten).catch(setFehler);
  }, []);

  const vorbelegen = async (id: string | null) => {
    setFehler(null);
    setGespeichert(false);

    try {
      await api.setzeStandardTaetigkeitsart(ich.mitarbeiter.id, id);
      /* Die Vorbelegung steckt in `ich` – nachladen, nicht die Seite neu laden. */
      await aktualisiereIch();
      setGespeichert(true);
    } catch (f) {
      setFehler(f);
    }
  };

  const meine = daten?.filter((t) => t.art === 'arbeit') ?? [];

  return (
    <>
      <h1 className="mb-1 text-2xl font-semibold">Vorbelegung</h1>
      <p className="mb-4 text-slate-600">
        Als was deine Stunden verrechnet werden – die Rolle, die in jeder neuen Stundenzeile
        vorbelegt steht. Welche Artikelnummer das im ERP ist, musst du nicht wissen.
      </p>

      <Fehler fehler={fehler} />

      <div className="karte">
        <label className="feldname" htmlFor="vorbelegung">
          Meine Vorbelegung
        </label>
        <select
          id="vorbelegung"
          className="feld sm:max-w-md"
          value={ich.standardTaetigkeitsartId ?? ''}
          onChange={(e) => void vorbelegen(e.target.value || null)}
        >
          <option value="">– keine –</option>
          {meine.map((t) => (
            <option key={t.id} value={t.id}>
              {t.gesellschaft} · {t.bezeichnung}
            </option>
          ))}
        </select>
        <p className="mt-1 text-sm text-slate-500">
          Steht in jeder neuen Stundenzeile – überschreibbar. Wer vormittags Projektleitung macht
          und nachmittags schraubt, ändert sie an der Zeile.
        </p>
        {gespeichert && <p className="mt-2 text-sm text-emerald-700">Gespeichert.</p>}
      </div>

      <h2 className="mt-6 mb-2 text-lg font-semibold">Welche Rollen es gibt</h2>

      {daten?.length === 0 && (
        <p className="text-slate-600">
          Noch keine angelegt – wende dich an die Leitung. Bis dahin gehen Stunden ohne
          Artikelnummer ins ERP.
        </p>
      )}

      <ul className="space-y-2">
        {daten?.map((t) => (
          <li key={t.id} className="karte">
            <div className="flex items-center gap-3">
              <span className="font-semibold text-slate-900">{t.bezeichnung}</span>
              <span className="ml-auto shrink-0 rounded border border-slate-300 px-2 py-0.5 text-xs tracking-wide text-slate-500 uppercase">
                {t.gesellschaft}
              </span>
            </div>
            <div className="mt-1 text-slate-700">
              {t.art === 'reise' ? 'Reise' : 'Arbeit'} · {t.einheit}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
